// =====================================================================================
// FILE: src/constants/entitlements.js
// 2026 Club Commercial Program - Entitlements Data
// Source: 2026_Club_Commercial_Program_Key_Changes_FINAL_INTERNAL.pdf
// =====================================================================================

/**
 * Branding types available for assets
 */
export const BRANDING_TYPES = {
  CLUB_IP: "100% Club IP",
  CO_BRANDED: "Co-branded (30/70)",
  COMMERCIAL: "Commercial",
  CO_BRANDED_OVERLAY: "Co-branded + Club Overlay",
};

/**
 * Key Club Moments categorization for 2026
 */
export const KEY_MOMENTS = {
  // Can be commercialized in 2026
  commercialisable: [
    "Host Welcome",
    "Interviews", 
    "Team Announcement",
    "Coin Toss",
    "Music Performance",
  ],
  // Must remain 100% Club IP
  clubIPOnly: [
    "Countdown",
    "Team Entry",
    "Club Song",
  ],
  // Fan engagement moments (co-branded)
  fanEngagement: [
    "Spot the Fan",
    "Noise Meter",
    "Kiss Cam",
    "Dance Cam",
  ],
};

/**
 * 2026 Club Entitlements structured by game phase
 * Maps to phases in phases.js
 */
export const ENTITLEMENTS_2026 = {
  // =========================================================================
  // PRE-GAME ENTITLEMENTS
  // =========================================================================
  preGame: {
    // Gates open to 30 minutes before first bounce
    gatesTo30Min: {
      label: "Gates Open → 30 min before bounce",
      led: {
        description: "LED full domination (field of play and parapet)",
        clubSOV: 90,
        aflSOV: 10,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CO_BRANDED_OVERLAY],
        notes: [
          "Excludes game information (score, time)",
          "AFL 10% can be used to promote AFLW, Gather Round etc.",
          "If AFL allocation not used, reverts to Club",
        ],
      },
      bigScreen: {
        description: "MCG Big Screens / Marvel Corner Screens",
        tvcMinutes: 2,
        maxTVCsPerPartner: 4,
        tvcDuration: 30,
        brandingOptions: [BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.COMMERCIAL],
      },
      superScreen: {
        description: "Marvel Super Screen",
        aspectRatios: ["20:9", "32:9"],
        requiresOverlay: true,
        notes: ["Live vision must suit each screen size"],
      },
    },
    // Within 30 minutes of first bounce
    within30Min: {
      label: "Within 30 min of bounce",
      led: {
        description: "LED full domination",
        clubSOV: 100,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CO_BRANDED_OVERLAY],
      },
      bigScreen: {
        description: "MCG Big Screens / Marvel Corner Screens",
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.COMMERCIAL],
        requiresApproval: true,
        approvalNote: "All graphics to be pre-approved by Lumen8",
      },
    },
    // Key Club Moments (pre-game)
    keyMoments: {
      label: "Key Club Moments",
      commercialisable: KEY_MOMENTS.commercialisable,
      clubIPOnly: KEY_MOMENTS.clubIPOnly,
      notes: [
        "Commercialisable moments can include partner branding",
        "Club IP moments must remain unbranded",
        "All graphics require Lumen8 pre-approval",
      ],
    },
  },

  // =========================================================================
  // IN-GAME ENTITLEMENTS
  // =========================================================================
  inGame: {
    // Standard Club Match Day Package
    matchDayPackage: {
      label: "Club Match Day Package",
      led: {
        description: "Club Partner Perimeter and Parapet LED",
        rotations: 24,
        duration: 30,
        totalMinutes: 12,
        timing: "siren to siren",
        notes: [
          "Must be bundled into partnerships inclusive of Club IP",
          "Cannot be provided to partner in isolation (no straight LED buy)",
          "Cannot be on-sold to other brands",
        ],
      },
      bigScreen: {
        clubStatic: {
          count: 3,
          duration: 5,
          perQuarter: true,
          brandingOptions: [BRANDING_TYPES.CLUB_IP],
        },
        commercialStatic: {
          count: 2,
          duration: 5,
          perQuarter: true,
          maxPerBrand: 4,
          brandingOptions: [BRANDING_TYPES.COMMERCIAL],
        },
      },
      superScreen: {
        description: "Single Side Panel",
        count: 5,
        duration: 10,
        perQuarter: true,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED],
        notes: ["Only for current and new major partners"],
      },
    },
    // Additional purchases available
    additionalPurchases: {
      label: "Additional LED Available",
      led: {
        additionalRotations: 16,
        duration: 30,
        timing: "siren to siren",
        notes: [
          "Must advise Lumen8 by 31 Jan",
          "Both MCG and Marvel must be purchased at equal spot weights",
          "Subject to availability",
          "Unsold inventory must be returned by 13 Feb for full refund",
        ],
      },
    },
    // Goal Animation
    goalAnimation: {
      label: "Home Goal Animation",
      optionA: {
        name: "Integrated Co-branded (Preferred)",
        ledDuration: 30,
        screenDuration: 10,
        ratio: "30/70",
        description: "Integrated co-branded goal celebration",
      },
      optionB: {
        name: "Split Format",
        ledClubDuration: 10,
        ledSponsorDuration: 20,
        screenClubDuration: 5,
        screenSponsorDuration: 5,
        holdEndFrame: true,
      },
    },
  },

  // =========================================================================
  // IN-BREAK ENTITLEMENTS (Quarter breaks)
  // =========================================================================
  inBreak: {
    quarterTime: {
      label: "Quarter Time Break",
      activationTime: "Entirety of break available",
      led: {
        clubSOV: 100,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CO_BRANDED_OVERLAY],
        notes: [
          "If LED integrated into activation (e.g. Race the LED), overlay not required",
          "Non-conflicting AFL Partners may request time (value exchange via Lumen8)",
        ],
      },
      bigScreen: {
        tvcMinutes: 1,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.COMMERCIAL],
      },
    },
    halfTime: {
      label: "Half Time Break",
      activationTime: "Final 4 minutes available",
      aflReserved: {
        until: "16 mins mark",
        programs: ["AusKick", "Rebel Rookies"],
        ledAllocation: "2 mins for AFL programs (e.g. AFLW)",
        notes: [
          "If AFL allocation not used, reverts to Club content",
          "No Club activation partner can conflict with AFL Partner program",
        ],
      },
      led: {
        clubSOV: 100,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CO_BRANDED_OVERLAY],
        availableTime: "Final 4 minutes",
        notes: ["Activation cannot run on field unless AFL approval provided"],
      },
      bigScreen: {
        tvcMinutes: 2,
        maxTVCsPerPartner: 4,
        tvcDuration: 30,
        notes: ["Preference for TVCs integrated with Club content (players/Club IP)"],
      },
    },
    threeQuarterTime: {
      label: "Three Quarter Time Break",
      activationTime: "Entirety of break available",
      led: {
        clubSOV: 100,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CO_BRANDED_OVERLAY],
      },
      bigScreen: {
        tvcMinutes: 1,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.COMMERCIAL],
      },
    },
  },

  // =========================================================================
  // POST-GAME ENTITLEMENTS
  // =========================================================================
  postGame: {
    sirenTo30Min: {
      label: "Final Siren → 30 min post",
      led: {
        clubSOV: 100,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CO_BRANDED_OVERLAY],
        description: "LED full domination (field of play and parapet)",
      },
      bigScreen: {
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.COMMERCIAL],
      },
      commercialisableMoments: ["Player Interview", "Crowd Celebration"],
      clubIPOnlyMoments: ["Team Song Post Siren", "Team Song from Changeroom"],
    },
    activation: {
      label: "Post-Game Activation (NEW in 2026)",
      duration: 2,
      timing: "After host wrap-up post-interviews",
      led: {
        clubSOV: 100,
        brandingOptions: [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CO_BRANDED_OVERLAY],
      },
      notes: [
        "Must have active fan engagement outcome",
        "Must not impact broadcasters",
        "If Kick 2 Kick in place, activation partner cannot conflict with K2K partner",
        "AFL Commercial Operations Guidelines must be adhered to",
      ],
    },
  },

  // =========================================================================
  // OTHER ASSET ENTITLEMENTS
  // =========================================================================
  otherAssets: {
    interchange: {
      label: "Interchange",
      entitlements: [
        "Home Club static signage on Home Club, Away Club and Umpire Bench",
        "LED parapet signage above bench (~10 sec every 120 secs during play)",
      ],
      maxPartners: 1,
      brandingRequirement: "Must include entitlement call out (e.g. 'Interchange brought to you by...')",
    },
    iptv: {
      label: "IPTV Screen Network",
      clubSOV: 20,
      example: "4 packs of 35 x 15 seconds = 140 x 15 second rotations",
      brandingOptions: [BRANDING_TYPES.COMMERCIAL, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CLUB_IP],
      formats: ["L-Frame", "Strap", "Full Screen take-over"],
      restrictions: ["No TVC", "No Audio", "No Club Auto partners"],
    },
    ponsfordStage: {
      label: "Ponsford Stage (MCG)",
      availability: "Fan engagement use for nominated Home games",
      ledUsage: {
        inBreak: BRANDING_TYPES.COMMERCIAL,
        inGame: BRANDING_TYPES.CLUB_IP,
      },
      restrictions: [
        "Unavailable for marquee games (ANZAC Eve, ANZAC Day, Queen's Birthday, Dreamtime)",
        "No Toyota competitor can use the stage",
        "Clubs must confirm fixture selection by Jan 31",
      ],
      hireFee: "$525 per game",
    },
    umpiresRace: {
      label: "Umpires Race (MCG)",
      cost: "Free",
      branding: BRANDING_TYPES.COMMERCIAL,
      restrictions: ["Cannot conflict with AFL Partner", "Club responsible for removal post game day"],
    },
    playersRace: {
      label: "Players Race (MCG)",
      branding: BRANDING_TYPES.CLUB_IP,
      restrictions: [
        "NO commercial content permitted on Player Race LED walls on gameday under any circumstances",
        "This is a strict MCG and AFL guideline — no exceptions",
        "If clubs supply commercial content for Player Race, escalate to Commercial Ops immediately",
      ],
    },
    outsideClubRoom: {
      label: "Outside Club Room (MCG)",
      branding: BRANDING_TYPES.CO_BRANDED,
      restrictions: [
        "Must be minimum 80% team branding / maximum 20% commercial",
        "100% commercial content is NOT permitted",
        "Changeroom screens are visible to broadcast cameras — non-compliant content will be seen on air",
        "If clubs supply 100% commercial content for changeroom screens, escalate to Commercial Ops immediately",
      ],
    },
    mcDesk: {
      label: "MC Desk (MCG)",
      branding: BRANDING_TYPES.CLUB_IP,
      cost: "Free",
    },
  },
};

/**
 * 2026 Key Changes Summary
 */
export const KEY_CHANGES_2026 = [
  {
    title: "30/70 Model Changes",
    description: "Co-branded creative can now feature Club IP overlay on top of 100% commercial partner branding",
    benefit: "Improved in-stadium look-and-feel, easier round-by-round updates",
  },
  {
    title: "Commercialisation of Club Moments",
    description: "Host welcome, interviews, team announcement, coin toss, music performance can now be commercialised",
    benefit: "Increased partnership asset suite for Clubs",
  },
  {
    title: "Expanded Activation Time",
    description: "Quarter time and 3QT: entirety of break available. Post-game: 2 minutes introduced",
    benefit: "More fan engagement opportunities",
  },
  {
    title: "AFL Promotional Time",
    description: "AFL receives 10% SOV during pre-game and half-time for promoting AFL programs",
    benefit: "No additional cost to Clubs, no game-by-game approval required",
  },
];

/**
 * Map phase labels to their entitlement sections
 */
export const PHASE_TO_ENTITLEMENT = {
  "Venue Entitlements": null, // No specific entitlements section
  "Ancillary Content": "otherAssets",
  "Pre-Game": "preGame",
  "In-Game (Quarters)": "inGame",
  "Quarter Breaks": "inBreak",
  "Post-Game": "postGame",
};

/**
 * Get entitlement summary for a phase
 * @param {string} phaseLabel - Phase label from phases.js
 * @returns {object|null} - Entitlement summary or null
 */
export function getEntitlementForPhase(phaseLabel) {
  const section = PHASE_TO_ENTITLEMENT[phaseLabel];
  if (!section) return null;
  return ENTITLEMENTS_2026[section] || null;
}

/**
 * Get quick tooltip text for a phase
 * @param {string} phaseLabel - Phase label
 * @returns {string} - Tooltip text
 */
export function getPhaseTooltip(phaseLabel) {
  switch (phaseLabel) {
    case "Pre-Game":
      return "LED: 90% Club / 10% AFL (gates→30min), 100% Club (30min→bounce) • Big Screen: 2min TVC • Key moments now commercialisable";
    case "In-Game (Quarters)":
      return "LED: 24×30sec rotations (12min total) • Goal animation: 30sec (30/70 or split) • Additional 16×30sec available for purchase";
    case "Quarter Breaks":
      return "QT/3QT: Full break available • HT: Final 4min (AusKick until 16min mark) • Activations must have fan engagement outcome";
    case "Post-Game":
      return "LED: 100% Club SOV • NEW: 2min activation window • Team song = 100% Club IP only";
    case "Ancillary Content":
      return "Interchange: 1 partner max • Ponsford Stage: In-break commercial, In-game Club IP • IPTV: 20% Club SOV";
    case "Venue Entitlements":
      return "Disney, AFL Marketing, MCG Venue content";
    default:
      return "";
  }
}

/**
 * Get branding options for a category
 * @param {string} category - Airtable category name
 * @returns {string[]} - Available branding options
 */
export function getBrandingOptionsForCategory(category) {
  const lowerCat = category.toLowerCase();
  
  // Team Song is always Club IP only
  if (lowerCat.includes("club song") || lowerCat.includes("team song") || lowerCat.includes("team entry")) {
    return [BRANDING_TYPES.CLUB_IP];
  }
  
  // Goal celebrations
  if (lowerCat.includes("goal")) {
    return [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED];
  }
  
  // Post-game win/lose
  if (lowerCat.includes("pg ") || lowerCat.includes("post")) {
    return [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.COMMERCIAL];
  }
  
  // Quarter breaks
  if (lowerCat.includes("qt") || lowerCat.includes("ht") || lowerCat.includes("3qt")) {
    return [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.CO_BRANDED_OVERLAY];
  }
  
  // Default - all options
  return [BRANDING_TYPES.CLUB_IP, BRANDING_TYPES.CO_BRANDED, BRANDING_TYPES.COMMERCIAL];
}
